//--------------------------------------------------------------------//
//******************** agentInfoTab **********************************//
//--------------------------------------------------------------------//
// Displays and manages editable agent properties: name, API key,
// model, and description. Each field supports inline edit/save/cancel
// with validation and error popups on failure. Also manages the admins
// list (other user_ids granted co-editor rights on this agent — see
// assertCanEdit in gudhub-agents' auth/authenticate.js), which auto-saves
// on change instead of using the edit/save/cancel flow.
import agentInfoTemplateUrl from './agent-info.template.html';
import { GUDHUB_SETTINGS } from '../../../config.js';

angular.module('agentsTableModule')

.directive('agentInfoTab', ['AgentsService', 'GhDialog', function (AgentsService, GhDialog) {
    return {
        restrict: 'E',
        scope: {
            agent: '=',
            onUpdate: '&'
        },
        controller: ['$scope', '$element', '$animate', 'localizationService', function ($scope, $element, $animate, localizationService) {
            const dictionary = localizationService.getDictionary('account_settings');
            $scope.translate = (word) => localizationService.translate(word, dictionary);

            // This tab swaps readonly <input>/<select> pairs in and out via ng-if as fields
            // enter/exit edit mode. ngAnimate (loaded globally) treats any ng-if swap between
            // elements sharing a CSS transition as an animation, keeping both the outgoing and
            // incoming element in the DOM until the transition would finish — which looked like
            // the old value and the new control rendering at the same time. This tab has no
            // enter/leave animations of its own, so disable ngAnimate for its whole subtree.
            $animate.enabled(false, $element);

            $scope.showApiKey = false;

            $scope.editMode = {
                name: false,
                api_key: false,
                description: false,
                model: false
            };

            // Current caller's GudHub user id, for the isOwner computation below.
            $scope.currentUserId = gudhub.storage.getUser().user_id;

            // Only the agent's creator may edit the admins list itself
            // (server-enforced in agent-manager.js's POST /update) — this is
            // purely a UI convenience so a non-owner admin doesn't see an
            // editable control the server will reject.
            $scope.isOwner = String($scope.agent.creator_user_id) === String($scope.currentUserId);

            // gh-input-list needs a real array to bind gh-model to (a
            // pre-existing agent created before the admins field existed
            // won't have one).
            $scope.agent.admins = $scope.agent.admins || [];

            $scope.originalValues = {
                name: $scope.agent.name || '',
                api_key: ($scope.agent.ai_model && $scope.agent.ai_model.api_key) || '',
                description: $scope.agent.description || '',
                model: ($scope.agent.ai_model && $scope.agent.ai_model.model) || ''
            };

            $scope.models = [];
            $scope.loadingModels = false;
            $scope.modelsError = '';

            const safeApply = function () {
                $scope.$applyAsync();
            };

            // Fetches the list of models available for the agent's current provider/API key,
            // mirroring the agent constructor's model step (agent_constructor_action.js fetchModels).
            const fetchModels = async function () {
                $scope.loadingModels = true;
                $scope.modelsError = '';
                $scope.models = [];

                try {
                    const providerId = $scope.agent.ai_model && $scope.agent.ai_model.ai_provider_id;
                    const apiKey = ($scope.agent.ai_model && $scope.agent.ai_model.api_key) || '';
                    const token = await gudhub.getToken();
                    const url = GUDHUB_SETTINGS.agents_server_url + '/providers/' + encodeURIComponent(providerId) +
                        '/models?api_key=' + encodeURIComponent(apiKey) + '&access_token=' + encodeURIComponent(token);

                    const response = await fetch(url);

                    if (!response.ok) {
                        const errBody = await response.json().catch(() => ({}));
                        throw new Error(errBody.message || errBody.error || 'Failed to load models.');
                    }

                    const rawModels = await response.json();
                    $scope.models = rawModels.map((m) => ({ name: m.name, value: m.id }));
                } catch (error) {
                    $scope.modelsError = error.message || 'Failed to load models.';
                } finally {
                    $scope.loadingModels = false;
                    safeApply();
                }
            };

            // Fetched proactively on load (not lazily on enableEdit) so the model list is
            // already available by the time the user clicks edit — avoids a visible
            // loading flash/layout jump right as the field switches into edit mode.
            fetchModels();

            // Enters edit mode for a field — stores original value and focuses the input.
            $scope.enableEdit = function (field) {
                if (field === 'api_key') {
                    $scope.originalValues[field] = ($scope.agent.ai_model && $scope.agent.ai_model.api_key) || '';
                } else if (field === 'model') {
                    $scope.originalValues[field] = ($scope.agent.ai_model && $scope.agent.ai_model.model) || '';
                } else {
                    $scope.originalValues[field] = $scope.agent[field] || '';
                }
                $scope.editMode[field] = true;

                setTimeout(function () {
                    if (field === 'name') {
                        const titleInput = document.querySelector('.agent_name_input');

                        if (titleInput) {
                            titleInput.removeAttribute('readonly');
                            titleInput.focus();
                        }
                    }

                    safeApply();
                });
            };

            // Exits edit mode and restores the field to its original value.
            $scope.cancelEdit = function (field) {
                if (field === 'api_key') {
                    $scope.agent.ai_model.api_key = $scope.originalValues[field];
                } else if (field === 'model') {
                    $scope.agent.ai_model.model = $scope.originalValues[field];
                } else {
                    $scope.agent[field] = $scope.originalValues[field];
                }
                $scope.editMode[field] = false;

                if (field === 'name') {
                    const titleInput = document.querySelector('.agent_name_input');

                    if (titleInput) {
                        titleInput.setAttribute('readonly', true);
                    }
                }

                safeApply();
                $scope.onUpdate();
            };

            // Saves the edited field to the server. Shows an error popup on failure.
            $scope.saveField = async function (field) {
                if (field === 'name' && (!$scope.agent.name || !$scope.agent.name.trim().length)) {
                    return false;
                }

                try {
                    const updateData = {
                        agent_id: $scope.agent._id
                    };

                    if (field === 'name') {
                        $scope.agent.name = $scope.agent.name.trim();
                        updateData.name = $scope.agent.name;
                    } else if (field === 'api_key') {
                        updateData.api_key = $scope.agent.ai_model.api_key;
                    } else if (field === 'description') {
                        updateData.description = $scope.agent.description || '';
                    } else if (field === 'model') {
                        updateData.model = $scope.agent.ai_model.model;
                    }

                    const updatedAgent = await AgentsService.update(updateData);

                    Object.assign($scope.agent, updatedAgent);

                    $scope.originalValues[field] = field === 'api_key'
                        ? ($scope.agent.ai_model && $scope.agent.ai_model.api_key) || ''
                        : field === 'model'
                            ? ($scope.agent.ai_model && $scope.agent.ai_model.model) || ''
                            : $scope.agent[field];

                    $scope.editMode[field] = false;

                    if (field === 'name') {
                        const titleInput = document.querySelector('.agent_name_input');

                        if (titleInput) {
                            titleInput.setAttribute('readonly', true);
                        }
                    }

                    safeApply();
                    $scope.onUpdate();
                } catch (error) {
                    GhDialog.show({
                        position: 'center',
                        toolbar: false,
                        template: {
                            toolbar: '',
                            resizable: false,
                            content: `
                                <div class="delete_agent-popup">
                                    <div class="dialog-share-group">
                                        <p class="dialog-share-group__title">Error</p>
                                    </div>
                                    <div class="dialog-share-group__content">
                                        <p class="info">{{errorMessage}}</p>
                                        <div class="dialog-share-group__content__buttons">
                                            <button class="btn btn-blue" ng-click="cancel()">OK</button>
                                        </div>
                                    </div>
                                </div>
                            `
                        },
                        locals: { errorMessage: error.message },
                        controller: ['$scope', 'errorMessage', function ($scope, errorMessage) {
                            $scope.errorMessage = errorMessage;
                        }]
                    });
                }
            };

            // gh-input-list mutates agent.admins in place by replacing it with a new
            // array reference on every add/remove, so a reference-watch fires exactly
            // once per user action (mirrors trunk-info.tab.js's admins watch). The
            // first invocation always has newVal === oldVal (same initial reference),
            // which must be skipped or every agent would issue a spurious update on
            // open. The response's admins is excluded from the merge-back below,
            // same reasoning as trunk-info.tab.js's applyTrunkUpdate: it's always a
            // fresh array instance, and re-assigning it would re-fire this same watch
            // (confirmed live as an infinite-PUT-request bug on the trunk tab).
            //
            // !isOwner is skipped outright: unlike trunk admins (any trunk admin can
            // edit trunk.admins), only the agent's creator may change agent.admins
            // (server-enforced in POST /update) - a non-owner's save attempt would
            // 403 on every single fire. Since reverting via `agent.admins = oldVal`
            // below is itself a reference change, letting the watch retry after that
            // 403 makes it re-fire, revert again, re-fire again - an infinite loop of
            // failed requests (confirmed live: dozens of failed /update calls for a
            // non-owner admin just viewing this read-only field). revertingAdmins
            // guards the same failure mode for the owner too, in case a save fails
            // for some other reason (e.g. a network blip).
            let revertingAdmins = false;
            $scope.$watch('agent.admins', async function (newVal, oldVal) {
                if (newVal === oldVal || !$scope.isOwner) return;
                if (revertingAdmins) {
                    revertingAdmins = false;
                    return;
                }

                try {
                    const updatedAgent = await AgentsService.update({ agent_id: $scope.agent._id, admins: newVal });
                    const { admins: _ignoredAdmins, ...rest } = updatedAgent;
                    Object.assign($scope.agent, rest);
                    $scope.onUpdate();
                } catch (error) {
                    revertingAdmins = true;
                    $scope.agent.admins = oldVal;
                    GhDialog.show({
                        position: 'center',
                        toolbar: false,
                        template: {
                            toolbar: '',
                            resizable: false,
                            content: `
                                <div class="delete_agent-popup">
                                    <div class="dialog-share-group">
                                        <p class="dialog-share-group__title">Error</p>
                                    </div>
                                    <div class="dialog-share-group__content">
                                        <p class="info">{{errorMessage}}</p>
                                        <div class="dialog-share-group__content__buttons">
                                            <button class="btn btn-blue" ng-click="cancel()">OK</button>
                                        </div>
                                    </div>
                                </div>
                            `
                        },
                        locals: { errorMessage: error.message },
                        controller: ['$scope', 'errorMessage', function ($scope, errorMessage) {
                            $scope.errorMessage = errorMessage;
                        }]
                    });
                } finally {
                    safeApply();
                }
            });

            // Opens a popup window with an iframe pointing at the app, authenticated via the agent's gudhub_auth_key.
            $scope.login = function () {
                if (!$scope.agent || !$scope.agent.gudhub_auth_key) {
                    return;
                }

                const targetUrl = `${window.location.origin}?auth=${encodeURIComponent($scope.agent.gudhub_auth_key)}`;
                const popup = window.open('', 'AgentLoginWin', 'width=1024,height=768,left=100,top=100,resizable=yes,scrollbars=yes');

                if (!popup) return;

                popup.document.title = 'Agent Login';

                const style = popup.document.createElement('style');
                style.textContent = 'html,body{margin:0;height:100%;}iframe{border:0;width:100%;height:100%;display:block;}';
                popup.document.head.appendChild(style);

                const iframe = popup.document.createElement('iframe');
                // Gives the iframe its own storage partition (localStorage/sessionStorage/cookies)
                // separate from this window's, so the agent's session can't overwrite our auth_key.
                iframe.credentialless = true;
                iframe.src = targetUrl;
                popup.document.body.appendChild(iframe);
            };
        }],
        templateUrl: agentInfoTemplateUrl
    };
}]);
